import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  RadioGroup,
  FormControlLabel,
  Radio,
  MenuItem,
  DialogContentText,
  Typography,
  Grid,
  Autocomplete,
  Box,
  Paper,
  Divider,
  Alert,
  List,
  Chip,
  IconButton
} from '@mui/material';
import { supabase } from '../../supabaseClient';
import { shareCaseDriveFolder } from '../../constants/CommonFunctions';
import { useOffice } from '../../store/officeContext';
import MemberCard from '../../components/Card/Member/MemberCard';
import { useTranslations } from '../../constants/translations/CaseTranslation';
import { Delete } from '@mui/icons-material';

export default function EditCaseModal({ open, onClose, caseData, onSave }) {
  const office = useOffice().office;
  
  // Obtener las traducciones basadas en el idioma del caso
  const t = useTranslations(caseData?.language || 'spanish');
  
  const [form, setForm] = useState({
    language: caseData?.language || 'spanish',
    number: caseData?.number || '',
    practice_area_id: caseData?.practice_area_id || '',
    hierarchy_form_id: caseData?.hierarchy_form_id || '',
    case_type_id: caseData?.case_type_id || '',
    office_status_id: caseData?.office_status_id || '',
    title: caseData?.title || '',
    description: caseData?.description || '',
    start_date: caseData?.start_date || '',
    end_date: caseData?.end_date || '',
  });

  // Estados para la jerarquía: práctica → form → tipo → estatus
  const [practiceAreas, setPracticeAreas] = useState([]);
  const [officeForms, setOfficeForms] = useState([]);
  const [caseTypes, setCaseTypes] = useState([]);
  const [officeStatuses, setOfficeStatuses] = useState([]);
  
  const [loadingPracticeAreas, setLoadingPracticeAreas] = useState(false);
  const [loadingForms, setLoadingForms] = useState(false);
  const [loadingCaseTypes, setLoadingCaseTypes] = useState(false);
  const [loadingStatuses, setLoadingStatuses] = useState(false);

  const [caseMembers, setCaseMembers] = useState([]); // Array de miembros actuales
  const [memberOptions, setMemberOptions] = useState([]); // Opciones de búsqueda
  const [loadingMembers, setLoadingMembers] = useState(false);
  const [currentMemberInput, setCurrentMemberInput] = useState(null);
  const [currentMemberRole, setCurrentMemberRole] = useState(null);
  const [membersToDelete, setMembersToDelete] = useState([]); // IDs de miembros a eliminar

  // Cargar miembros del caso cuando se abre el modal
  useEffect(() => {
    const loadCaseMembers = async () => {
      if (!open || !caseData?.id) return;

      try {
        const { data, error } = await supabase
          .from('case_member')
          .select(`
            id,
            person_id,
            name_str,
            role,
            person:person_id (
              id,
              full_name,
              email,
              document_number,
              phone_number
            )
          `)
          .eq('case_id', caseData.id);

        if (error) throw error;

        // Transformar datos para el estado local
        const transformedMembers = (data || []).map(member => ({
          id: member.id, // ID del registro case_member (para eliminar)
          person_id: member.person_id,
          name_str: member.name_str,
          role: member.role,
          full_name: member.person_id ? member.person.full_name : member.name_str,
          email: member.person_id ? member.person.email : null,
          document_number: member.person_id ? member.person.document_number : null,
          phone_number: member.person_id ? member.person.phone_number : null,
        }));

        setCaseMembers(transformedMembers);
      } catch (error) {
        console.error('Error loading case members:', error);
      }
    };

    loadCaseMembers();
  }, [open, caseData?.id]);

  // Cargar áreas práctica del despacho
  useEffect(() => {
    const fetchPracticeAreas = async () => {
      if (!office?.id) return;
      
      setLoadingPracticeAreas(true);
      try {
        const { data, error } = await supabase
          .from('office_hierarchy')
          .select(`
            hierarchy:hierarchy_id (
              id,
              practice_area (
                id,
                name
              )
            )
          `)
          .eq('office_id', office.id);

        if (error) throw error;

        const areasMap = new Map();

        data?.forEach(item => {
          const area = item.hierarchy?.practice_area;
          if (area && !areasMap.has(area.id)) {
            areasMap.set(area.id, {
              id: area.id,
              practice_area_id: area.id,
              name: area.name
            });
          }
        });

        setPracticeAreas(Array.from(areasMap.values()));
      } catch (error) {
        console.error('Error fetching practice areas:', error);
      } finally {
        setLoadingPracticeAreas(false);
      }
    };

    if (open && office?.id) {
      fetchPracticeAreas();
    }
  }, [open, office?.id]);

  // Cargar forms cuando cambia el área práctica
  useEffect(() => {
    const fetchOfficeForms = async () => {
      if (!form.practice_area_id) {
        setOfficeForms([]);
        setCaseTypes([]);
        setOfficeStatuses([]);
        return;
      }

      setLoadingForms(true);
      try {
        const { data, error } = await supabase
          .from('office_hierarchy')
          .select(`
            hierarchy:hierarchy_id (
              id,
              practice_area_id,
              hierarchy_form (
                id,
                name,
                display_order
              )
            )
          `)
          .eq('office_id', office.id);

        if (error) throw error;

        const forms = (data || [])
          .filter(item => item.hierarchy?.practice_area_id === form.practice_area_id)
          .flatMap(item => item.hierarchy?.hierarchy_form || [])
          .sort((a, b) => (a.display_order ?? 0) - (b.display_order ?? 0));

        setOfficeForms(forms);
      } catch (error) {
        console.error('Error fetching office forms:', error);
        setOfficeForms([]);
      } finally {
        setLoadingForms(false);
      }
    };

    if (open) {
      fetchOfficeForms();
    }
  }, [open, form.practice_area_id]);

  // Cargar tipos de expediente cuando cambia la forma
  useEffect(() => {
    const fetchCaseTypes = async () => {
      if (!form.hierarchy_form_id) {
        setCaseTypes([]);
        setOfficeStatuses([]);
        return;
      }

      setLoadingCaseTypes(true);
      try {
        const { data, error } = await supabase
          .from('case_type')
          .select('id, name')
          .eq('hierarchy_form_id', form.hierarchy_form_id);

        if (error) throw error;

        setCaseTypes(data || []);
      } catch (error) {
        console.error('Error fetching case types:', error);
        setCaseTypes([]);
      } finally {
        setLoadingCaseTypes(false);
      }
    };

    if (open) {
      fetchCaseTypes();
    }
  }, [open, form.hierarchy_form_id]);

  // Cargar estatus cuando cambia la forma o el tipo
  useEffect(() => {
    const fetchOfficeStatuses = async () => {
      if (!form.hierarchy_form_id) {
        setOfficeStatuses([]);
        return;
      }

      setLoadingStatuses(true);
      try {
        let query = supabase
          .from('office_status')
          .select('id, name, display_order')
          .eq('form_id', form.hierarchy_form_id)
          .order('display_order');

        if (caseTypes.length > 0 && !form.case_type_id) {
          setOfficeStatuses([]);
          setLoadingStatuses(false);
          return;
        }

        if (caseTypes.length > 0 && form.case_type_id) {
          query = query.eq('type_id', form.case_type_id);
        } else if (caseTypes.length === 0) {
          query = query.is('type_id', null);
        }

        const { data: statusData, error } = await query;

        if (error) throw error;

        setOfficeStatuses(statusData || []);
      } catch (error) {
        console.error('Error fetching office statuses:', error);
        setOfficeStatuses([]);
      } finally {
        setLoadingStatuses(false);
      }
    };

    if (open) {
      fetchOfficeStatuses();
    }
  }, [open, form.hierarchy_form_id, form.case_type_id, caseTypes.length]);

  useEffect(() => {
    if (open && caseData) {
      // Resetear estados de miembros
      setMembersToDelete([]);
      setCurrentMemberInput(null);
      setCurrentMemberRole(null);
      
      // Actualizar el formulario con los datos del caso
      setForm({
        language: caseData.language || 'spanish',
        number: caseData.number || '',
        practice_area_id: caseData?.practice_area_id || '',
        hierarchy_form_id: caseData.hierarchy_form_id || '',
        case_type_id: caseData.case_type_id || '',
        office_status_id: caseData.office_status_id || '',
        title: caseData.title || '',
        description: caseData.description || '',
        start_date: caseData.start_date || '',
        end_date: caseData.end_date || '',
      });
      console.log(form)
    }
  }, [open, caseData]);

  const fetchMemberOptions = async (input, roleFilter = null) => {
    if (input.length < 3) return;

    try {
      let query = supabase
        .from('office_member')
        .select(`
          id,
          job_position,
          role,
          person:member_id (*)
        `)
        .eq('office_id', office.id);

      if (roleFilter) {
        query = query.eq('role', roleFilter);
      }

      const { data, error } = await query.limit(10);

      if (!error && data) {
        const transformedData = data.map(item => ({
          id: item.person.id,
          full_name: item.person.full_name,
          email: item.person.email,
          document_number: item.person.document_number,
          phone_number: item.person.phone_number,
          role: item.role,
          job_position: item.job_position
        }));
        setMemberOptions(transformedData);
      } else {
        setMemberOptions([]);
      }
    } catch (error) {
      console.error('Error fetching member options:', error);
      setMemberOptions([]);
    }
  };

  const handleAddMember = () => {
    if (!currentMemberInput) {
      alert('Debe ingresar un nombre o seleccionar una persona');
      return;
    }

    const isObject = typeof currentMemberInput === 'object';
    const personId = isObject ? currentMemberInput.id : null;
    
    // Validar que no se repita
    if (personId && caseMembers.some(m => m.person_id === personId)) {
      alert('Esta persona ya fue agregada');
      return;
    }

    // Validaciones de rol
    const clientCount = caseMembers.filter(m => m.role === 'client').length;
    const lawyerCount = caseMembers.filter(m => m.role === 'lawyer').length;

    if (currentMemberRole === 'client' && clientCount >= 1) {
      alert('Solo puede haber un cliente por expediente');
      return;
    }

    if (currentMemberRole === 'lawyer' && lawyerCount >= 1) {
      alert('Solo puede haber un socio/abogado principal por expediente');
      return;
    }

    const newMember = {
      // NO tiene id (es nuevo, no existe en BD aún)
      person_id: personId,
      name_str: isObject ? null : currentMemberInput,
      role: currentMemberRole,
      full_name: isObject ? currentMemberInput.full_name : currentMemberInput,
      email: isObject ? currentMemberInput.email : null,
      document_number: isObject ? currentMemberInput.document_number : null,
      phone_number: isObject ? currentMemberInput.phone_number : null,
      isNew: true // Marcador para identificar miembros nuevos
    };

    setCaseMembers([...caseMembers, newMember]);
    setCurrentMemberInput(null);
    setCurrentMemberRole(null);
    setMemberOptions([]);
  };

  const handleRemoveMember = (index) => {
    const memberToRemove = caseMembers[index];
    
    // Si el miembro tiene id, agregarlo a la lista de eliminación
    if (memberToRemove.id && !memberToRemove.isNew) {
      setMembersToDelete([...membersToDelete, memberToRemove.id]);
    }
    
    // Remover del estado local
    const updatedMembers = caseMembers.filter((_, i) => i !== index);
    setCaseMembers(updatedMembers);
  };

  const getRoleLabel = (role) => {
    const labels = {
      spanish: {
        client: 'Cliente',
        lawyer: 'Abogado',
        partner: 'Colaborador'
      },
      english: {
        client: 'Client',
        lawyer: 'Lawyer',
        partner: 'Partner'
      }
    };
    const lang = caseData?.language === 'english' ? 'english' : 'spanish';
    return labels[lang][role] || role;
  };

  const handleChange = (field, value) => {
    setForm(prev => {
      const newForm = { ...prev, [field]: value };
      
      // Si cambia el área práctica, limpiar campos dependientes
      if (field === 'practice_area_id') {
        newForm.hierarchy_form_id = '';
        newForm.case_type_id = '';
        newForm.office_status_id = '';
      }
      
      // Si cambia la forma, limpiar tipo y estatus
      if (field === 'hierarchy_form_id') {
        newForm.case_type_id = '';
        newForm.office_status_id = '';
      }

      // Si cambia el tipo, limpiar estatus
      if (field === 'case_type_id') {
        newForm.office_status_id = '';
      }
      
      return newForm;
    });
  };

  const handleSubmit = async () => {
    try {
      // 1. PRIMERO eliminar miembros marcados para eliminación
      if (membersToDelete.length > 0) {
        const { error: deleteError } = await supabase
          .from('case_member')
          .delete()
          .in('id', membersToDelete);

        if (deleteError) {
          console.error('Error eliminando miembros:', deleteError);
          throw deleteError;
        }
      }

      // 2. LUEGO insertar nuevos miembros
      const newMembers = caseMembers.filter(m => m.isNew);
      if (newMembers.length > 0) {
        const membersToInsert = newMembers.map(member => ({
          case_id: caseData.id,
          person_id: member.person_id,
          name_str: member.name_str,
          role: member.role
        }));

        const { error: insertError } = await supabase
          .from('case_member')
          .insert(membersToInsert);

        if (insertError) {
          console.error('Error insertando miembros:', insertError);
          throw insertError;
        }

        shareCaseDriveFolder(caseData.id);
      }

      // 3. FINALMENTE actualizar datos básicos del caso y recargar todo
      const formData = {
        ...form,
      };
      
      await onSave(formData);

      // Limpiar estados
      setMembersToDelete([]);
      onClose();
    } catch (error) {
      console.error('Error al guardar cambios:', error);
      alert('Error al guardar los cambios del expediente');
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xl" fullWidth>
      <DialogTitle>
        <Typography variant="h5" fontWeight="600" component="div">
          {t.editCase}
        </Typography>
        <DialogContentText component="div" sx={{ mt: 1 }}>
          {t.editCaseDescription}
        </DialogContentText>
      </DialogTitle>
      
      <DialogContent dividers>
        <Grid container spacing={3}>
          {/* Columna izquierda - Información del expediente */}
          <Grid item size={{ xs: 12, md: 8 }}>
            {/* Información Básica */}
            <Paper elevation={0} sx={{ p: 2, mb: 3, bgcolor: 'grey.50' }}>
              <Typography variant="subtitle1" fontWeight="600" gutterBottom>
                {t.basicInfo || 'Información Básica'}
              </Typography>
              <Divider sx={{ mb: 2 }} />
              
              <Grid container spacing={2}>
                <Grid item size={{ xs: 12, sm: 8 }}>
                  <TextField
                    label={t.caseNumber}
                    value={form.number}
                    onChange={(e) => handleChange('number', e.target.value)}
                    fullWidth
                    disabled
                  />
                </Grid>

                <Grid item size={{ xs: 12, sm: 4 }}>
                  <Box>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      {t.caseLanguage}
                    </Typography>
                    <RadioGroup
                      row
                      value={form.language}
                      onChange={(e) => handleChange('language', e.target.value)}
                    >
                      <FormControlLabel value="spanish" control={<Radio />} label={t.spanish} />
                      <FormControlLabel value="english" control={<Radio />} label={t.english} />
                    </RadioGroup>
                  </Box>
                </Grid>

                <Grid item size={{ xs: 12 }}>
                  <TextField
                    label={t.title}
                    value={form.title}
                    onChange={(e) => handleChange('title', e.target.value)}
                    fullWidth
                  />
                </Grid>

                <Grid item size={{ xs: 12 }}>
                  <TextField
                    label={t.description}
                    value={form.description}
                    onChange={(e) => handleChange('description', e.target.value)}
                    fullWidth
                    multiline
                    rows={3}
                  />
                </Grid>
              </Grid>
            </Paper>

            {/* Clasificación */}
            <Paper elevation={0} sx={{ p: 2, mb: 3, bgcolor: 'grey.50' }}>
              <Typography variant="subtitle1" fontWeight="600" gutterBottom>
                {t.classification || 'Clasificación'}
              </Typography>
              <Divider sx={{ mb: 2 }} />
              
              <Grid container spacing={2}>
                <Grid item size={{ xs: 12, sm: 6 }}>
                  <TextField
                    label={t.practiceArea || "Área Práctica"}
                    value={form.practice_area_id}
                    onChange={(e) => handleChange('practice_area_id', e.target.value)}
                    fullWidth
                    select
                    disabled={loadingPracticeAreas}
                  >
                    {practiceAreas.map((area) => (
                      <MenuItem key={area.id} value={area.id}>
                        {area.name}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>

                <Grid item size={{ xs: 12, sm: 6 }}>
                  <TextField
                    label={t.form || "Forma"}
                    value={form.hierarchy_form_id}
                    onChange={(e) => handleChange('hierarchy_form_id', e.target.value)}
                    fullWidth
                    select
                    disabled={!form.practice_area_id || loadingForms}
                    helperText={!form.practice_area_id ? (t.selectPracticeAreaFirst || "Seleccione primero un área práctica") : ''}
                  >
                    {officeForms.map((formItem) => (
                      <MenuItem key={formItem.id} value={formItem.id}>
                        {formItem.name}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>

                {caseTypes.length > 0 && (
                  <Grid item size={{ xs: 12, sm: 6 }}>
                    <TextField
                      label={t.caseType}
                      value={form.case_type_id}
                      onChange={(e) => handleChange('case_type_id', e.target.value)}
                      fullWidth
                      select
                      disabled={!form.hierarchy_form_id || loadingCaseTypes}
                      helperText={!form.hierarchy_form_id ? (t.selectFormFirst || "Seleccione primero una forma") : ''}
                    >
                      {caseTypes.map((type) => (
                        <MenuItem key={type.id} value={type.id}>
                          {type.name}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                )}

                <Grid item size={{ xs: 12, sm: caseTypes.length > 0 ? 6 : 12 }}>
                  <TextField
                    label={t.status}
                    value={form.office_status_id}
                    onChange={(e) => handleChange('office_status_id', e.target.value)}
                    fullWidth
                    select
                    disabled={
                      !form.hierarchy_form_id || 
                      loadingStatuses || 
                      (caseTypes.length > 0 && !form.case_type_id)
                    }
                  >
                    {officeStatuses.map((status) => (
                      <MenuItem key={status.id} value={status.id}>
                        {status.name}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
              </Grid>
            </Paper>

            {/* Fechas */}
            <Paper elevation={0} sx={{ p: 2, mb: 3, bgcolor: 'grey.50' }}>
              <Typography variant="subtitle1" fontWeight="600" gutterBottom>
                {t.dates || 'Fechas'}
              </Typography>
              <Divider sx={{ mb: 2 }} />
              
              <Grid container spacing={2}>
                <Grid item size={{ xs: 12, sm: 6 }}>
                  <TextField
                    type="date"
                    label={t.startDate || 'Fecha de inicio'}
                    value={form.start_date}
                    onChange={(e) => handleChange('start_date', e.target.value)}
                    fullWidth
                    InputLabelProps={{
                      shrink: true,
                    }}
                  />
                </Grid>

                <Grid item size={{ xs: 12, sm: 6 }}>
                  <TextField
                    type="date"
                    label={`${t.endDate || 'Fecha de finalización'} (${t.optional || 'Opcional'})`}
                    value={form.end_date}
                    onChange={(e) => handleChange('end_date', e.target.value)}
                    fullWidth
                    InputLabelProps={{
                      shrink: true,
                    }}
                  />
                </Grid>
              </Grid>
            </Paper>
          </Grid>

          {/* Columna derecha - Partes Involucradas */}
          <Grid item size={{ xs: 12, md: 4 }}>
            <Paper elevation={0} sx={{ p: 2, bgcolor: 'grey.50', position: 'sticky', top: 20 }}>
              <Typography variant="subtitle1" fontWeight="600" gutterBottom>
                {t.parties || 'Partes Involucradas'}
              </Typography>
              <Divider sx={{ mb: 2 }} />

              {/* Formulario para agregar miembro */}
              <Box sx={{ mb: 3 }}>
                <Grid container spacing={2}>
                  {/* Selector de rol */}
                  <Grid item size={{xs:12}}>
                    <TextField
                      select
                      label={t.memberRole || 'Rol'}
                      value={currentMemberRole}
                      onChange={(e) => setCurrentMemberRole(e.target.value)}
                      fullWidth
                      size="medium"
                    >
                      <MenuItem 
                        value="client"
                        disabled={caseMembers.some(m => m.role === 'client')}
                      >
                        Cliente
                      </MenuItem>
                      <MenuItem 
                        value="lawyer"
                        disabled={caseMembers.some(m => m.role === 'lawyer')}
                      >
                        Abogado
                      </MenuItem>
                      <MenuItem value="partner">
                        Colaborador
                      </MenuItem>
                    </TextField>
                  </Grid>

                  {/* Autocomplete - copiar del CaseRegisterView */}
                  <Grid item size={{xs:12}}>
                    <Autocomplete
                      freeSolo
                      loading={loadingMembers}
                      value={currentMemberInput}
                      inputValue={
                        typeof currentMemberInput === 'string' 
                          ? currentMemberInput 
                          : currentMemberInput 
                            ? `${currentMemberInput.full_name.replace(';', '')} | ${currentMemberInput.email || currentMemberInput.document_number}` 
                            : ''
                      }
                      onInputChange={(e, value, reason) => {
                        // Permitir que el input cambie libremente solo cuando el usuario escribe
                        if (reason === 'input') {
                          // Si el usuario está escribiendo, convertir a string
                          setCurrentMemberInput(value);
                        }
                        
                        // No limpiar cuando se selecciona una opción
                        if (reason === 'reset') {
                          // Mantener el valor actual
                          return;
                        }
                        
                        // Buscar opciones cuando hay 3+ caracteres
                        if (value && value.length >= 3) {
                          setLoadingMembers(true);
                          fetchMemberOptions(value, currentMemberRole).finally(() => 
                            setLoadingMembers(false)
                          );
                        }
                      }}
                      onChange={(e, value, reason) => {
                        // Manejar cuando se selecciona una opción del dropdown
                        if (reason === 'selectOption') {
                          setCurrentMemberInput(value);
                        }
                        // Manejar cuando se limpia
                        else if (reason === 'clear') {
                          setCurrentMemberInput(null);
                        }
                      }}
                      options={memberOptions}
                      getOptionLabel={(option) =>
                        typeof option === 'string'
                          ? option
                          : `${option.full_name.replace(';', '')} | ${option.email || option.document_number}`
                      }
                      isOptionEqualToValue={(option, value) => {
                        if (typeof option === 'string' && typeof value === 'string') {
                          return option === value;
                        }
                        if (typeof option === 'object' && typeof value === 'object') {
                          return option.id === value.id;
                        }
                        return false;
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label={t.searchMember}
                          fullWidth
                          size="medium"
                        />
                      )}
                    />
                    {/* Indicador de estado del miembro */}
                    {currentMemberInput && (
                      <Typography
                        variant="caption"
                        color={typeof currentMemberInput === 'object' ? 'success.main' : 'warning.main'}
                        sx={{ mt: 0.5, display: 'block' }}
                      >
                        {typeof currentMemberInput === 'object' 
                          ? t.userIdentified 
                          : t.userNotIdentified
                        }
                      </Typography>
                    )}
                    
                    {currentMemberInput && typeof currentMemberInput === 'string' && (
                      <Alert severity="info" sx={{ mt: 1 }}>
                        {caseData?.language === 'spanish' 
                          ? 'Este miembro se agregará como texto. Podrá registrarlo posteriormente y vincularlo al expediente.'
                          : 'This member will be added as text. You can register them later and link them to the case.'
                        }
                      </Alert>
                    )}
                  </Grid>

                  {/* Botón agregar */}
                  <Grid item size={{xs:12}}>
                    <Button
                      variant="contained"
                      fullWidth
                      onClick={handleAddMember}
                      disabled={!currentMemberInput}
                    >
                      Agregar
                    </Button>
                  </Grid>
                </Grid>
              </Box>

              <Divider sx={{ mb: 2 }} />

              {/* Lista de miembros*/}
              <Box>
                <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
                  Miembros del Expediente
                </Typography>
                
                {caseMembers.length === 0 ? (
                  <Alert severity="info" sx={{ mt: 2 }}>
                    No se han agregado miembros
                  </Alert>
                ) : (
                  <List>
                    {caseMembers.map((member, index) => (
                    <Paper 
                      key={index} 
                      elevation={1} 
                      sx={{ p: 2, mb: 2, bgcolor: 'grey.50' }}
                    >
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        <Box sx={{ flex: 1 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
                            <Typography variant="body1" fontWeight="600">
                              {member.full_name.replace(';', '')}
                            </Typography>
                            
                            {/* Chip de rol */}
                            <Chip 
                              label={getRoleLabel(member.role)} 
                              size="small"
                              color={office.color}
                            />
                            
                            <Chip 
                              label={member.person_id ? t.systemUser : t.textLabel}
                              size="small"
                              variant={member.person_id ? "filled" : "outlined"}
                              color={office.color}
                              sx={{ 
                                height: 20,
                                fontSize: '0.65rem'
                              }}
                            />
                          </Box>
                          
                          {member.email && (
                            <Typography variant="caption" color="text.secondary" display="block">
                              ✉️ {member.email}
                            </Typography>
                          )}
                          {member.document_number && (
                            <Typography variant="caption" color="text.secondary" display="block">
                              🆔 {member.document_number}
                            </Typography>
                          )}
                          {member.phone_number && (
                            <Typography variant="caption" color="text.secondary" display="block">
                              📞 {member.phone_number}
                            </Typography>
                          )}
                          
                          {!member.person_id && (
                            <Alert severity="info" sx={{ mt: 1, py: 0 }}>
                              <Typography variant="caption">
                                {caseData?.language === 'spanish'
                                  ? 'Podrá registrarse y vincularse posteriormente'
                                  : 'Can be registered and linked later'
                                }
                              </Typography>
                            </Alert>
                          )}
                        </Box>
                        <IconButton 
                          size="small" 
                          onClick={() => handleRemoveMember(index)}
                          color="error"
                        >
                          <Delete />
                        </IconButton>
                      </Box>
                    </Paper>
                  ))}
                  </List>
                )}
              </Box>
            </Paper>
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} size="large">
          {t.cancel}
        </Button>
        <Button variant="contained" onClick={handleSubmit} size="large">
          {t.saveChanges}
        </Button>
      </DialogActions>
    </Dialog>
  );
}